import { useCallback, useEffect, useRef, useState } from "react";
import history from "utils/history";
import viewpaths from "global/constants/ViewPathConstants";
import strings from "global/constants/StringConstants";
import {
  fillCopy,
  openErrorNotification,
  openInfoNotification,
  openSuccessNotification,
} from "helpers/methods";
import {
  getAiDesign,
  getAiSession,
  getAiTemplateSummary,
  getAiVersions,
  hideAiSession,
  listAiSessions,
  publishAiDesign,
  restoreAiVersion,
  sendAiMessage,
  startAiSession,
} from "./AiWebFormService";
import {
  AiFailure,
  aiBuilderPath,
  aiErrorMessage,
  aiFailure,
  documentChip,
  historyEntries,
  labelWarnings,
  newPageTexts,
  retryAfterMs,
  startedSession,
} from "./AiWebFormHelper";
import {
  AiBuilderParams,
  AiDesign,
  AiPublishResponse,
  AiTemplateSummary,
  AiVersionList,
  AiWarning,
  ChatEntry,
} from "./AiWebFormTypesAndValidation";

const copy = strings.aiWebForm;

/** Messages per history page. */
const PAGE_SIZE = 30;
/** After a timed-out start, how often and how long the session list is checked for it. */
const START_RECOVERY_POLL_MS = 5_000;
const START_RECOVERY_TRIES = 14;
/** The template signer's sessions read before a start (the AI's largest page), and while recovering it. */
const SESSIONS_BEFORE_START = 50;
const RECOVERY_PAGE = 5;
/** After a timed-out turn, how long to wait before looking for it in the history. */
const TURN_RECOVERY_WAIT_MS = 5_000;

export type AiBuilderPhase =
  | "loading"
  | "starting"
  | "ready"
  | "startFailed"
  | "loadFailed"
  | "notFound"
  | "switchedOff";

/** What a publish came to: the publication, or how it failed and the AI's or core's message. */
export type AiPublishOutcome =
  | { published: AiPublishResponse; failure?: undefined }
  | { published?: undefined; failure: AiFailure; message: string };

export interface AiBuilderState {
  phase: AiBuilderPhase;
  error: string;
  sessionId: string | null;
  designId: string | null;
  title: string;
  publishedVersion: number | null;
  /** The current version (what a message changes and Publish publishes). */
  current: AiDesign | null;
  /** Another version shown in the preview from the versions list. */
  previewing: AiDesign | null;
  entries: ChatEntry[];
  nextBefore: number | null;
  loadingEarlier: boolean;
  turnRunning: boolean;
  /** The design moved under the sender (409): the banner is up. */
  stale: boolean;
  versions: AiVersionList | null;
  template: AiTemplateSummary | null;
  /** Page words collected this visit, for a publish when the AI does not report them per version. */
  pageTexts: string[];
  labelWarnings: AiWarning[];
}

const initialState: AiBuilderState = {
  phase: "loading",
  error: "",
  sessionId: null,
  designId: null,
  title: "",
  publishedVersion: null,
  current: null,
  previewing: null,
  entries: [],
  nextBefore: null,
  loadingEarlier: false,
  turnRunning: false,
  stale: false,
  versions: null,
  template: null,
  pageTexts: [],
  labelWarnings: [],
};

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const mergeTexts = (into: string[], add: string[] | undefined) =>
  Array.from(new Set([...into, ...(add ?? [])]));

let localId = 0;

/**
 * The build screen's state: start or reopen a design session, one chat turn at
 * a time, versions, restore and publish. Every AI answer is shown as the AI
 * wrote it; the browser decides only how to recover (a busy 429, a stale 409,
 * a timeout that may still have landed).
 */
export const useAiWebForm = (params: AiBuilderParams) => {
  const [state, setState] = useState<AiBuilderState>(initialState);
  const stateRef = useRef(state);
  stateRef.current = state;
  /** The session id (or "new") this page has already opened, so a re-render never opens it twice. */
  const openedRef = useRef<string | null>(null);
  const mountedRef = useRef(true);
  useEffect(() => {
    // Set on every mount too: StrictMode's dev remount runs the cleanup once in between.
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const patch = useCallback((update: Partial<AiBuilderState>) => {
    if (mountedRef.current) setState((s) => ({ ...s, ...update }));
  }, []);

  const loadTemplate = useCallback(
    async (designId: string | null) => {
      try {
        const template = await getAiTemplateSummary({
          templateId: params.templateId,
          signerId: params.signerId,
          designId,
        });
        if (mountedRef.current) {
          setState((s) => ({ ...s, template, entries: decorateOpening(s.entries, template) }));
        }
      } catch {
        // The header falls back to the name it was opened with; nothing else depends on it.
      }
    },
    [params.templateId, params.signerId],
  );

  const loadVersions = useCallback(
    async (designId: string) => {
      try {
        const versions = await getAiVersions(designId);
        patch({ versions, publishedVersion: versions.publishedVersion });
        return versions;
      } catch {
        return null;
      }
    },
    [patch],
  );

  /**
   * The session's latest page of history, the current design and its versions. Resolves to what was
   * loaded (null when it was not), for a caller that needs it at once: `stateRef` catches up only when
   * the page renders, which under a React 18 root (createRoot) comes after the caller's next line.
   */
  const loadSession = useCallback(
    async (
      sessionId: string,
      options: { quiet?: boolean } = {},
    ): Promise<{ design: AiDesign; entries: ChatEntry[] } | null> => {
      if (!options.quiet) patch({ phase: "loading" });
      try {
        const detail = await getAiSession(sessionId, { limit: PAGE_SIZE });
        const [design, versions] = await Promise.all([
          getAiDesign(detail.designId),
          getAiVersions(detail.designId).catch(() => null),
        ]);
        if (!mountedRef.current) return null;
        const entries = historyEntries(detail.messages, versions?.versions ?? []);
        setState((s) => ({
          ...s,
          phase: "ready",
          sessionId: detail.sessionId,
          designId: detail.designId,
          title: detail.title,
          publishedVersion: versions?.publishedVersion ?? detail.publishedVersion,
          current: design,
          previewing: null,
          entries: decorateOpening(entries, s.template),
          nextBefore: detail.nextBefore,
          versions,
          labelWarnings: design.labelWarnings ?? s.labelWarnings,
        }));
        loadTemplate(detail.designId);
        return { design, entries };
      } catch (error: any) {
        const failure = aiFailure(error);
        if (failure === "switchedOff") patch({ phase: "switchedOff" });
        else if (failure === "notFound") patch({ phase: "notFound" });
        else if (!options.quiet) {
          patch({ phase: "loadFailed", error: aiErrorMessage(error) || copy.loadFailed });
        }
        return null;
      }
    },
    [patch, loadTemplate],
  );

  const showStarted = useCallback(
    (sessionId: string) => {
      // The address now names the session, so a reload reopens it instead of starting again.
      openedRef.current = sessionId;
      history.replace(aiBuilderPath({ ...params, sessionId }));
    },
    [params],
  );

  /** The ids of the template signer's sessions, read before a start; null when they could not be read. */
  const sessionsBefore = useCallback(async (): Promise<ReadonlySet<string> | null> => {
    try {
      const list = await listAiSessions({
        templateId: params.templateId,
        signerId: params.signerId,
        limit: SESSIONS_BEFORE_START,
      });
      return new Set(list.sessions.map((session) => session.sessionId));
    } catch {
      return null;
    }
  }, [params.templateId, params.signerId]);

  /**
   * A start that timed out may still have made the session: look for it before giving up. Without
   * the sessions from before the start it cannot be told from an older one, so nothing is adopted.
   */
  const recoverStart = useCallback(
    async (before: ReadonlySet<string> | null) => {
      if (!before) return false;
      for (let attempt = 0; attempt < START_RECOVERY_TRIES && mountedRef.current; attempt++) {
        await wait(START_RECOVERY_POLL_MS);
        try {
          const list = await listAiSessions({
            templateId: params.templateId,
            signerId: params.signerId,
            limit: RECOVERY_PAGE,
          });
          const started = startedSession(list.sessions, before);
          if (started) {
            showStarted(started.sessionId);
            await loadSession(started.sessionId);
            return true;
          }
        } catch {
          // Keep looking until the tries run out.
        }
      }
      return false;
    },
    [params.templateId, params.signerId, showStarted, loadSession],
  );

  const start = useCallback(async () => {
    const prompt = params.prompt?.trim() || null;
    setState((s) => ({
      ...initialState,
      phase: "starting",
      template: s.template,
      entries: [openingEntry(s.template, prompt)],
    }));
    const before = await sessionsBefore();
    try {
      const started = await startAiSession({
        templateId: params.templateId,
        signerId: params.signerId,
        prompt,
      });
      if (!mountedRef.current) return;
      setState((s) => ({
        ...s,
        phase: "ready",
        sessionId: started.sessionId,
        designId: started.designId,
        title: s.template?.templateName ?? params.name,
        current: started.design,
        entries: [
          { ...openingEntry(s.template, prompt), id: "t1-u" },
          {
            kind: "assistant",
            id: "t1-a",
            text: started.reply,
            at: started.createdAt,
            outcome: started.design.version > 1 ? "changed" : null,
            designVersion: started.design.version,
            turn: started.turn,
            changes: started.changes,
            warnings: started.warnings.filter(
              (w) => !w.code.startsWith("label_") && w.key == null,
            ),
          },
        ],
        pageTexts: mergeTexts([], started.changes?.pageTexts),
        labelWarnings: labelWarnings(started.warnings),
      }));
      showStarted(started.sessionId);
      loadVersions(started.designId);
      loadTemplate(started.designId);
    } catch (error: any) {
      const failure = aiFailure(error);
      if (failure === "switchedOff") {
        patch({ phase: "switchedOff" });
        return;
      }
      if (failure === "busy" || failure === "retryable") {
        if (await recoverStart(before)) return;
      }
      patch({ phase: "startFailed", error: aiErrorMessage(error) || copy.startFailed });
    }
  }, [params, patch, showStarted, loadVersions, loadTemplate, sessionsBefore, recoverStart]);

  // Open: a session named in the address is reopened; "new" starts one. Once per
  // address: the latest callbacks are read through a ref, so a re-render (the
  // page rebuilds its params from the URL) never opens or starts it again.
  const openRef = useRef({ loadSession, loadTemplate, start });
  openRef.current = { loadSession, loadTemplate, start };
  useEffect(() => {
    const key = params.sessionId ?? "new";
    if (openedRef.current === key) return;
    openedRef.current = key;
    if (params.sessionId) {
      if (params.sessionId !== stateRef.current.sessionId) {
        openRef.current.loadSession(params.sessionId);
      }
    } else {
      // The document chip needs the template's numbers before the start answers.
      openRef.current.loadTemplate(null).finally(() => openRef.current.start());
    }
  }, [params.sessionId]);

  const loadEarlier = useCallback(async () => {
    const { sessionId, nextBefore, versions } = stateRef.current;
    if (!sessionId || nextBefore == null) return;
    patch({ loadingEarlier: true });
    try {
      const page = await getAiSession(sessionId, { limit: PAGE_SIZE, before: nextBefore });
      const older = historyEntries(page.messages, versions?.versions ?? []);
      setState((s) => {
        const known = new Set(s.entries.map((e) => e.id));
        return {
          ...s,
          entries: decorateOpening(
            [...older.filter((e) => !known.has(e.id)), ...s.entries],
            s.template,
          ),
          nextBefore: page.nextBefore,
          loadingEarlier: false,
        };
      });
    } catch (error: any) {
      patch({ loadingEarlier: false });
      openErrorNotification(aiErrorMessage(error) || copy.loadFailed);
    }
  }, [patch]);

  /** Everything again after the design moved under the sender; resolves to what was loaded. */
  const reloadLatest = useCallback(async () => {
    const { sessionId } = stateRef.current;
    return sessionId ? loadSession(sessionId, { quiet: true }) : null;
  }, [loadSession]);

  /**
   * One chat turn. Resolves true when the message was taken (the composer stays
   * empty), false when it should go back in the box to be sent again.
   * If options.displayText is provided, that text is shown in the chat instead of `text`.
   */
  const send = useCallback(
    async (text: string, options?: { displayText?: string }): Promise<boolean> => {
      const { sessionId, current, turnRunning } = stateRef.current;
      if (!sessionId || turnRunning) return false;
      const pendingId = `local-${(localId += 1)}`;
      const lastTurn = maxTurn(stateRef.current.entries);
      const display = options?.displayText ?? text;
      setState((s) => ({
        ...s,
        turnRunning: true,
        stale: false,
        entries: [
          ...s.entries,
          {
            kind: "user",
            id: pendingId,
            text: display,
            at: new Date().toISOString(),
            byMe: true,
          },
        ],
      }));
      const dropPending = () =>
        setState((s) => ({ ...s, entries: s.entries.filter((e) => e.id !== pendingId) }));
      try {
        const answer = await sendAiMessage(sessionId, {
          message: text,
          baseVersion: current?.version ?? null,
        });
        if (!mountedRef.current) return true;
        setState((s) => {
          const changed = answer.outcome === "changed" && !!answer.design.html;
          return {
            ...s,
            turnRunning: false,
            current: changed
              ? { ...answer.design, origin: "ai_turn", createdAt: new Date().toISOString() }
              : s.current,
            previewing: changed ? null : s.previewing,
            pageTexts: changed ? mergeTexts(s.pageTexts, answer.changes?.pageTexts) : s.pageTexts,
            entries: [
              ...s.entries.map((e) => (e.id === pendingId ? { ...e, id: `t${answer.turn}-u` } : e)),
              {
                kind: "assistant",
                id: `t${answer.turn}-a`,
                text: answer.reply,
                at: new Date().toISOString(),
                outcome: answer.outcome,
                designVersion: answer.design.version,
                turn: answer.turn,
                changes: answer.changes,
                warnings: answer.warnings,
                retryText: answer.outcome === "kept" ? text : undefined,
              },
            ],
          };
        });
        if (answer.outcome === "changed") loadVersions(stateRef.current.designId ?? "");
        return true;
      } catch (error: any) {
        const failure = aiFailure(error);
        const message = aiErrorMessage(error);
        if (failure === "stale") {
          dropPending();
          const latest = await reloadLatest();
          patch({ stale: true, turnRunning: false });
          const version = latest?.design.version;
          if (version) openInfoNotification(fillCopy(copy.staleToast, { version }));
          return false;
        }
        if (failure === "switchedOff") {
          patch({ phase: "switchedOff", turnRunning: false });
          return false;
        }
        if (failure === "notFound") {
          patch({ phase: "notFound", turnRunning: false });
          return false;
        }
        if (failure === "busy") {
          // Another turn on this session is running (maybe a colleague's): show it once it lands.
          dropPending();
          if (message) openInfoNotification(message);
          await wait(retryAfterMs(error));
          await reloadLatest();
          patch({ turnRunning: false });
          return false;
        }
        if (failure === "retryable") {
          // The turn may still have landed: look for it before asking for it again.
          await wait(TURN_RECOVERY_WAIT_MS);
          const latest = await reloadLatest();
          patch({ turnRunning: false });
          const landed = !!latest && maxTurn(latest.entries) > lastTurn;
          if (landed) return true;
          dropPending();
          openErrorNotification(message || copy.loadFailed);
          return false;
        }
        dropPending();
        // Another 409 is a limit, or a stale version worded otherwise: either way the next send needs the
        // latest version, or it repeats the same baseVersion and gets the same 409.
        if (failure === "conflict") await reloadLatest();
        patch({ turnRunning: false });
        openErrorNotification(message || copy.loadFailed);
        return false;
      }
    },
    [patch, reloadLatest, loadVersions],
  );

  const preview = useCallback(
    async (version: number | null) => {
      const { designId, current } = stateRef.current;
      if (!designId || version == null || version === current?.version) {
        patch({ previewing: null });
        return;
      }
      try {
        patch({ previewing: await getAiDesign(designId, version) });
      } catch (error: any) {
        openErrorNotification(aiErrorMessage(error) || copy.loadFailed);
      }
    },
    [patch],
  );

  const restore = useCallback(
    async (version: number) => {
      const { designId } = stateRef.current;
      if (!designId) return;
      try {
        const restored = await restoreAiVersion(designId, version);
        setState((s) => ({
          ...s,
          current: restored,
          previewing: null,
          stale: false,
          // The new version shows at once; the refetch below replaces the list with the AI's own.
          versions: s.versions && !s.versions.versions.some((v) => v.version === restored.version)
            ? {
                ...s.versions,
                currentVersion: restored.version,
                // Oldest first, as the AI lists them.
                versions: [
                  ...s.versions.versions,
                  {
                    version: restored.version,
                    origin: "restore",
                    turn: null,
                    restoredFrom: version,
                    sha256: restored.sha256,
                    size: restored.size ?? 0,
                    createdAt: restored.createdAt ?? new Date().toISOString(),
                    byMe: true,
                  },
                ],
              }
            : s.versions,
          entries: [
            ...s.entries,
            {
              kind: "system",
              id: `restore-v${restored.version}`,
              text: fillCopy(copy.restoredByMe, { version }),
              at: restored.createdAt ?? new Date().toISOString(),
            },
          ],
        }));
        loadVersions(designId);
        openSuccessNotification(fillCopy(copy.restored, { version, next: restored.version }));
      } catch (error: any) {
        const failure = aiFailure(error);
        if (failure === "stale" || failure === "conflict") await reloadLatest();
        openErrorNotification(aiErrorMessage(error) || copy.loadFailed);
      }
    },
    [loadVersions, reloadLatest],
  );

  /**
   * What the publish dialog lists for a version: the page words signers would
   * newly see, and the fields whose values would not reach the document.
   */
  const publishChecks = useCallback(async (version: number) => {
    const { designId, current, publishedVersion, pageTexts, labelWarnings: known } = stateRef.current;
    if (!designId) return { pageTexts: [] as string[], labelWarnings: known };
    try {
      const target = version === current?.version ? current : await getAiDesign(designId, version);
      if (target?.pageTexts) {
        const published =
          publishedVersion != null && publishedVersion !== version
            ? (await getAiDesign(designId, publishedVersion)).pageTexts
            : publishedVersion === version
              ? target.pageTexts
              : [];
        return {
          pageTexts: newPageTexts(target.pageTexts, published),
          labelWarnings: target.labelWarnings ?? known,
        };
      }
    } catch {
      // Fall back to what this visit collected.
    }
    return { pageTexts, labelWarnings: known };
  }, []);

  /** Null when there is no design to publish; a failure is left to the screen, which may ask again. */
  const publish = useCallback(
    async (
      version: number,
      name: string,
      replaceExisting: boolean,
    ): Promise<AiPublishOutcome | null> => {
      const { designId, sessionId } = stateRef.current;
      if (!designId || !sessionId) return null;
      try {
        const published = await publishAiDesign(designId, {
          version,
          sessionId,
          templateId: params.templateId,
          signerId: params.signerId,
          name,
          replaceExisting,
        });
        patch({ publishedVersion: published.publication.version });
        loadVersions(designId);
        openSuccessNotification(
          fillCopy(copy.published, { version: published.publication.version }),
        );
        return { published };
      } catch (error: any) {
        return { failure: aiFailure(error), message: aiErrorMessage(error) };
      }
    },
    [params.templateId, params.signerId, patch, loadVersions],
  );

  const hide = useCallback(async () => {
    const { sessionId } = stateRef.current;
    if (!sessionId) return;
    try {
      await hideAiSession(sessionId);
      openSuccessNotification(copy.removed);
      history.push(viewpaths.webFormsViewPath);
    } catch (error: any) {
      openErrorNotification(aiErrorMessage(error) || copy.loadFailed);
    }
  }, []);

  const dismissStale = useCallback(() => patch({ stale: false }), [patch]);

  const retry = useCallback(() => {
    const { phase, sessionId } = stateRef.current;
    if (phase === "startFailed") start();
    else if (sessionId || params.sessionId) loadSession((sessionId ?? params.sessionId) as string);
  }, [start, loadSession, params.sessionId]);

  return {
    state,
    send,
    preview,
    restore,
    publish,
    publishChecks,
    hide,
    loadEarlier,
    dismissStale,
    retry,
  };
};

const maxTurn = (entries: ChatEntry[]) =>
  entries.reduce(
    (max, e) => (e.kind === "assistant" && e.turn != null && e.turn > max ? e.turn : max),
    0,
  );

/** The start's user line, with the document it was built from and the opening style. */
const openingEntry = (template: AiTemplateSummary | null, prompt: string | null): ChatEntry => ({
  kind: "user",
  id: "opening",
  text: copy.openingLine,
  at: new Date().toISOString(),
  byMe: true,
  document: documentChip(template),
  styleLine: prompt ? fillCopy(copy.styleLine, { style: prompt }) : undefined,
});

/**
 * The start read back from history looks as it did live: the AI keeps the opening style itself as the
 * first message ("Create a webform for this document." only when none was typed), so that text goes back
 * under the opening line, and the document chip returns once the template summary is in.
 */
const decorateOpening = (entries: ChatEntry[], template: AiTemplateSummary | null) =>
  entries.map((e) => {
    if (e.kind !== "user" || (e.id !== "t1-u" && e.id !== "opening")) return e;
    const opening =
      e.id === "t1-u" && e.text !== copy.openingLine && !e.styleLine
        ? { ...e, text: copy.openingLine, styleLine: fillCopy(copy.styleLine, { style: e.text }) }
        : e;
    return opening.document ? opening : { ...opening, document: documentChip(template) };
  });
